import React, { useEffect } from 'react'
import classNames from 'classnames'
import Logo from '#src/components/Logo'
import { Button } from '@deal/components'
import AttributedHtmlText from '#src/components/AttributedHtmlText'
import MetaData, { MetaDataComponentMap } from '#src/components/Path/MetaData'
import InputHeader from '#src/components/Path/components/InputHeader'
import QuestionHeader from '#src/components/Path/components/QuestionHeader'
import PageHeader from '#src/components/Path/components/PageHeader'
import { Experiment } from '@deal/experiment-js'
import { extractPageInterstitialData, extractGenericModalMetadata } from '#src/services/path'
import ErrorHeader from '#src/components/Path/components/ErrorHeader'
import Interstitial, { InterstitialMap } from '#src/components/Path/Interstitials'
import styles from './styles.css'
import { LayoutProps } from '../'
import {
  InterstitialPathFlowPageMetadataType,
  InputTextPathFlowQuestionFragment,
  NumericPathFlowQuestionFragment,
  ListPathFlowQuestionFragment
} from '#src/generated/graphql'
import Question, { QuestionComponentMap, PathQuestionProps } from '../../Question'
import { usePathContext } from '#src/components/Path/PathContext'

// Questions
import InputTextPathFlowQuestionComponent from '#src/components/Path/components/InputTextPathFlowQuestion'
import IdPathFlowQuestionComponent from '#src/components/Path/components/IdPathFlowQuestion'
import ListPathFlowQuestionComponent from '#src/components/Path/components/ListPathFlowQuestion'
import NumericPathFlowQuestionComponent from '#src/components/Path/components/NumericPathFlowQuestion'
import OAuthEmailPathFlowQuestionComponent from '#src/components/Path/components/OAuthEmailPathFlowQuestion'
import DateRangePathFlowQuestionComponent from '#src/components/Path/components/DateRangePathFlowQuestion'
import DatePathFlowQuestionComponent from '#src/components/Path/components/DatePathFlowQuestion'

// Interstitials
import GenericInterstitial from '#src/components/Path/components/GenericInterstitial'
import GenericWithButtonInterstitial from '#src/components/Path/components/GenericWithButtonInterstitial'
import RequestPushNotificationInterstitial from '#src/components/Path/components/RequestPushNotificationsInterstitial'
import WhatHappensNextInterstitial from '#src/components/Path/components/WhatHappensNextInterstitial'

// Metadata
import TermsPrivacyMedicalMetadata from '#src/components/Path/components/TermsPrivacyMedicalMetadata'
import TermsAndConditionsMetadata from '#src/components/Path/components/TermsAndConditionsMetadata'
import ContactPrivacyMetadata from '#src/components/Path/components/ContactPrivacyMetadata'
import ContactTCPAComplianceMetadata from '#src/components/Path/components/ContactTCPAComplianceMetadata'
import PhoneExplanationModalMetadata from '#src/components/Path/components/PhoneExplanationModalMetadata'
import GenericInfoBoxPageMetadata from '#src/components/Path/components/GenericInfoBoxPageMetadata'
import RatingContactPageMetadataHeader from '#src/components/Path/components/RatingContactPageMetadata/Header'
import RatingContactPageMetadataFooter from '#src/components/Path/components/RatingContactPageMetadata/Footer'
import ReviewFooterPageMetadata from '#src/components/Path/components/ReviewFooterPageMetadata'

const ListPathFlowQuestionComponentWrapper: React.SFC<PathQuestionProps<
  ListPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <ListPathFlowQuestionComponent {...rest} display="standard" />
}

const InputTextPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  InputTextPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <InputTextPathFlowQuestionComponent {...rest} display="standard" />
}

const NumericPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  NumericPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <NumericPathFlowQuestionComponent {...rest} display="standard" />
}

interface OverrideProps {
  questionMap?: QuestionComponentMap
  interstitialMap?: InterstitialMap
  metaDataMap?: MetaDataComponentMap
}

interface DefaultLayoutProps {
  hideLogo?: boolean
}

const DefaultLayout: React.SFC<LayoutProps & DefaultLayoutProps & OverrideProps> = ({
  areAllQuestionsAnswered,
  areAllQuestionsOptional,
  className,
  questions,
  hideLogo,
  onUpdateScrollRef,
  questionMap: overrideQuestionMap,
  interstitialMap: overrideInterstitialMap,
  metaDataMap: overrideMetaDataMap
}) => {
  const questionMap: QuestionComponentMap = overrideQuestionMap || {
    InputTextPathFlowQuestion: InputTextPathFlowQuestionWrapper,
    IdPathFlowQuestion: IdPathFlowQuestionComponent,
    ListPathFlowQuestion: ListPathFlowQuestionComponentWrapper,
    NumericPathFlowQuestion: NumericPathFlowQuestionWrapper,
    OAuthEmailPathFlowQuestion: OAuthEmailPathFlowQuestionComponent,
    DateRangePathFlowQuestion: DateRangePathFlowQuestionComponent,
    DatePathFlowQuestion: DatePathFlowQuestionComponent
  }

  const interstitialMap: InterstitialMap = overrideInterstitialMap || {
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_TITLE]: GenericInterstitial,
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_BUTTON]: GenericWithButtonInterstitial,
    [InterstitialPathFlowPageMetadataType.REQUEST_PUSH_NOTIFICATION_PERMISSION]: RequestPushNotificationInterstitial,
    [InterstitialPathFlowPageMetadataType.WHAT_HAPPENS_NEXT]: WhatHappensNextInterstitial
  }

  const metaDataMap: MetaDataComponentMap = overrideMetaDataMap || {
    TermsPrivacyMedicalPathFlowPageMetadata: TermsPrivacyMedicalMetadata,
    TermsAndConditionsPathFlowPageMetadata: TermsAndConditionsMetadata,
    ContactPrivacyMetadata,
    ContactTCPAComplianceMetadata,
    PhoneExplanationModalMetadata,
    GenericInfoBoxPageMetadata
  }

  useEffect(() => {
    onUpdateScrollRef(window)
  }, [])

  const pathContext = usePathContext()

  const topLevelCategory = pathContext.pathFlow.category
    ? pathContext.pathFlow.category.department
      ? pathContext.pathFlow.category.department.topLevelCategory
      : pathContext.pathFlow.category
    : undefined
  const areActionsHidden = pathContext.isSubmitButtonHidden && pathContext.isBackButtonHidden
  const submitButtonText = pathContext.pathFlowPage.submitButtonText
    ? pathContext.pathFlowPage.submitButtonText.text
    : 'Submit'
  const skipButtonText = pathContext.pathFlowPage.skipButtonText
    ? pathContext.pathFlowPage.skipButtonText.text
    : 'Skip'

  const interstitialMetadata = extractPageInterstitialData(pathContext.pathFlowPage.metadata)
  const genericModalMetadata = extractGenericModalMetadata(pathContext.pathFlowPage.metadata)

  return (
    <>
      <div
        className={classNames('render-path-default', styles.pageContainer, styles.fixedBody)}
        data-testid="path"
      >
        <div className={classNames(styles.pageContainer, className)}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${pathContext.pathFlowPage.completionPercentage}%` }}
            />
          </div>

          {!hideLogo && (
            <div className={styles.logo}>
              <Logo className={styles.logoMark} color="dark" mark="word" />
            </div>
          )}

          <div className={styles.container}>
            <ErrorHeader />

            <PageHeader className={styles.title} />

            {pathContext.pathFlowPage.notificationTexts.map(notification => (
              <h2 className={styles.subtitle} key={notification.text}>
                <AttributedHtmlText text={notification} />
              </h2>
            ))}

            {interstitialMetadata && (
              <div className={styles.interstitial}>
                <Interstitial
                  key={pathContext.pathFlowPage.internalName}
                  interstitialMap={interstitialMap}
                  submitButtonText={submitButtonText}
                />
              </div>
            )}

            <RatingContactPageMetadataHeader
              categorySlug={topLevelCategory && topLevelCategory.slug}
            />

            {/* Do not render questions if we are also showing interstitial */}
            {/* This is mostly a hack for the request-notifications question for native app users */}
            {/* We want to defer all logic to custom interstitial and avoid rendering default path questions */}
            {questions.length > 0 && !interstitialMetadata && (
              <Experiment id="default-layout-action-placement" default="default">
                {({ result: experimentResult }) => (
                  <form
                    onSubmit={!pathContext.isSubmitting ? pathContext.onPageSubmitted : undefined}
                    className={styles.form}
                  >
                    <div
                      className={classNames(styles.formContainer, {
                        [styles.defaultFormContainer]: experimentResult !== 'relative'
                      })}
                    >
                      <div>
                        {questions.map((question, index) => {
                          return (
                            <React.Fragment key={index}>
                              <QuestionHeader
                                questionText={question.questionText}
                                questionSubText={question.questionSubText}
                                genericModalMetadata={genericModalMetadata}
                              />
                              <InputHeader
                                inputLabel={question.inputLabel}
                                inputIconImage={question.inputIconImage}
                              />
                              <Question
                                key={question.internalName}
                                questionIndex={index}
                                question={question}
                                questionComponentMap={questionMap}
                              />
                            </React.Fragment>
                          )
                        })}
                      </div>
                      <div>
                        <RatingContactPageMetadataFooter />
                        <MetaData
                          categorySlug={topLevelCategory && topLevelCategory.slug}
                          metaDataComponents={metaDataMap}
                        />
                      </div>
                    </div>
                    {!areActionsHidden && (
                      <div
                        className={classNames(styles.actions, {
                          [styles.defaultActions]: experimentResult !== 'relative'
                        })}
                      >
                        <div
                          className={classNames(styles.buttons, {
                            [styles.centerContent]: pathContext.isSubmitButtonHidden
                          })}
                        >
                          {!pathContext.isBackButtonHidden && (
                            <div>
                              <Button
                                data-testid="back-button"
                                className={styles.back}
                                type="button"
                                size="large"
                                variant="contrast-light"
                                tabIndex={2}
                                onClick={pathContext.onPageBack}
                                disabled={pathContext.isSubmitting}
                              >
                                {pathContext.isSubmitting && pathContext.isSubmitButtonHidden
                                  ? 'Submitting...'
                                  : 'Back'}
                              </Button>
                            </div>
                          )}
                          {!pathContext.isSubmitButtonHidden && (
                            <div className={styles.submit}>
                              <Button
                                data-testid="submit-button"
                                type="button"
                                size="large"
                                tabIndex={1}
                                disabled={!pathContext.isFormValid || pathContext.isSubmitting}
                                onClick={() => pathContext.onPageSubmitted()}
                              >
                                {pathContext.isSubmitting
                                  ? 'Submitting...'
                                  : areAllQuestionsOptional
                                  ? areAllQuestionsAnswered
                                    ? submitButtonText
                                    : skipButtonText
                                  : submitButtonText}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </form>
                )}
              </Experiment>
            )}
          </div>
        </div>
      </div>
      <ReviewFooterPageMetadata />
    </>
  )
}

export default React.memo(DefaultLayout)
